import React from "react";
import moment from "moment";
import { Link } from "react-router";
import { RaisedButton, RadioButtonGroup, RadioButton } from "material-ui";
import * as colors from "material-ui/styles/colors";

import Select, { Creatable } from "react-select";
import WorkingIndicator from "./partials/working-indicator.jsx";

import StatsBlockContainer from "./partials/stats-block-container.jsx";
import StatsBlock from "./partials/stats-block.jsx";
import Label from "./partials/label.jsx";
import AddLineItem from "./partials/billing/add-line-item.jsx";
import LinkLike from "./partials/link-like.jsx";

import connectToStores from "alt-utils/lib/connectToStores";
import UserStore from "../stores/UserStore";
import UserActions from "../actions/UserActions";
import GodActions from "../actions/GodActions";
import UserUtil from "../utils/UserUtil";
import BillingUtil from "../utils/BillingUtil";
import { formatPrice, capitalize, unSnakeCase } from "../util.js";

import GenericAnalyticsTable from "./partials/generic-analytics-table.jsx";
import UserEditorModal from "./partials/user-editor-modal.jsx";
import InlineNoticeLabel from "./partials/inline-notice-label.jsx";

import qs from "query-string";
const parsedQueryString = qs.parse(window.location.search);

const BILLING_TIMEZONE = "America/Chicago";

class Billing extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      summary: null,

      selectedMonth:
        parsedQueryString.month ||
        moment().tz(BILLING_TIMEZONE).format("MM-YYYY"),
      invoicePreview: null,
      invoiceDisplayMode: "summary",

      billingEmails: [],

      isAttemptingComp: null,
      isEditingLineItem: null,

      isFinalizing: false
    };

    this._fakeEventBucket = this._fakeEventBucket.bind(this);
  }

  static getStores() {
    return [UserStore];
  }

  static getPropsFromStores() {
    return UserStore.getState();
  }

  static get contextTypes() {
    return {
      router: React.PropTypes.object
    };
  }

  activateVirtual() {
    UserUtil.generateVirtualActivationLink({
      apiKey: UserStore.getState().apiKey
    }).then(({ url }) => {
      window.location.href = `${url}&next=${encodeURIComponent(
        window.location.href
      )}`;
    });
  }

  manageVirtual() {
    UserUtil.generateVirtualManagementLink({
      apiKey: UserStore.getState().apiKey,
      next: window.location.href
    }).then(({ url }) => {
      window.location.href = url;
    });
  }

  updateSubscription(opts) {
    this.setState({ isUpdating: true });
    BillingUtil.updateSettings(UserStore.getState().apiKey, opts)
      .then(() => this._loadSummary())
      .then(() => this.setState({ isUpdating: false }))
      .catch(e => window.alert(`Update failed: ${e.message}`));
  }

  performAdjustment(opts) {
    let adjustment = {
      action: opts.action,
      amount: opts.amount,
      currency: opts.currency,
      date: opts.date,
      description: opts.description,
      target: opts.target,
      owner: UserStore.getUserId()
    };
    BillingUtil.performAdjustment(UserStore.getState().apiKey, adjustment)
      .then(() => {
        this.setState({
          isAttemptingComp: null,
          isEditingLineItem: null
        });
        this._loadInvoicePreview({ quiet: true });
      })
      .catch(e => window.alert(`Adjustment failed: ${e.message}`));
  }

  finalize() {
    this.setState({ loading: true, isFinalizing: true });
    BillingUtil.pushInvoiceToStripe(UserStore.getState().apiKey, {
      month: this.state.selectedMonth
    })
      .then(({ invoice }) => {
        this.setState({
          loading: false,
          isFinalizing: false,
          invoicePreview: invoice
        });
      })
      .catch(e => {
        this.setState({
          loading: false,
          isFinalizing: false
        });
        window.alert(`Finalization failed: ${e.message}`);
      });
  }

  _loadSummary() {
    return BillingUtil.fetchBillingSummary(UserStore.getState().apiKey).then(
      data => {
        this.setState({ summary: data });
      }
    );
  }

  _loadInvoicePreview({ quiet } = {}) {
    let selectingMonth = this.state.selectedMonth;
    let start = moment
      .tz(this.state.selectedMonth, "MM-YYYY", BILLING_TIMEZONE)
      .startOf("month");
    let end = moment
      .tz(this.state.selectedMonth, "MM-YYYY", BILLING_TIMEZONE)
      .endOf("month");

    this.setState(
      Object.assign({ loading: true }, !quiet && { invoicePreview: null })
    );

    BillingUtil.fetchInvoicePreview(UserStore.getState().apiKey, {
      start,
      end
    })
      .then(({ preview }) => {
        if (this.state.selectedMonth === selectingMonth) {
          this.setState({
            loading: false,
            invoicePreview: preview
          });
        }
      })
      .catch(e => window.alert(e.message));
  }

  _fakeEventBucket({ name, timezone } = {}) {
    GodActions.createFakeEventBucket({
      user: UserStore.getState().user.id,
      name: name,
      timezone: timezone
    });
    this.setState({ showGodUserSettings: false });
  }

  componentWillReceiveProps(newProps) {
    if (!this.state.loading && !this.state.invoicePreview) {
      this._loadSummary();
      this._loadInvoicePreview();
    }
  }

  componentDidMount() {
    UserActions.fetchUser.defer();
    this.componentWillReceiveProps(this.props);
  }

  _generateMonths() {
    let curr = moment
      .tz("02-2021", "MM-YYYY", BILLING_TIMEZONE) // first month of ledger billing = Feb 2021
      .startOf("month");
    let end = moment().tz(BILLING_TIMEZONE).startOf("month");

    let months = [];

    while (curr) {
      if (curr <= end) {
        months.push({
          value: curr.format("MM-YYYY"),
          label:
            curr.diff(end) === 0
              ? moment().tz(BILLING_TIMEZONE).format("MMMM [1]–D, YYYY")
              : curr.format("MMMM YYYY"),
          _end: moment(curr).endOf("month")
        });
        curr.add(1, "month");
      } else {
        curr = null;
      }
    }

    return months.reverse();
  }

  render() {
    const months = this._generateMonths(),
      { invoiceDisplayMode, invoicePreview, summary } = this.state;

    const invoiceRawMonth = months.find(
      m => m.value === this.state.selectedMonth
    );
    const invoiceLabel = invoiceRawMonth && invoiceRawMonth.label;

    let lineItems;

    if (this.state.invoicePreview) {
      if (invoiceDisplayMode === "summary") {
        lineItems = {};
        for (const currency in invoicePreview.line_items) {
          const items = invoicePreview.line_items[currency];
          let groups = {};
          for (const i of items) {
            let category = i.editable_id ? i.editable_id : i.category;

            if (!groups[category]) {
              groups[category] = {
                category: i.category,
                description: [],
                count: 0,
                amount: 0,
                currency
              };
            }

            groups[category].description.push(
              `${i.description}${
                i.summary_count ? ` (${i.summary_count})` : ""
              }`
            );
            if (!i.editable_id) {
              groups[category].count += i.summary_count || 1;
            }
            groups[category].amount += i.amount;
          }
          lineItems[currency] = Object.values(groups);
        }
      } else {
        lineItems = invoicePreview.line_items;
      }
    }

    const isBillingGod = UserStore.isBillingGod();
    const user = UserStore.getState().user;

    const latestInvoiceBlurb =
      summary &&
      summary.subscription_data &&
      summary.subscription_data.latest_invoice ? (
        <div style={{ fontSize: 12, marginTop: 5 }}>
          <a
            href={summary.subscription_data.latest_invoice.link}
            target="_blank"
          >
            Download{" "}
            {moment(summary.subscription_data.latest_invoice.date_paid).format(
              "l"
            )}{" "}
            receipt for US$
            {(
              summary.subscription_data.latest_invoice.amount_due / 100
            ).toFixed(2)}
          </a>
        </div>
      ) : null;

    return (
      <div>
        {this.state.showGodUserSettings ? (
          <UserEditorModal
            user={user}
            roles={UserStore.getState().roles}
            onSave={data => {
              UserActions.godEditUser(user.id, data);
              this.setState({ showGodUserSettings: false });
              setTimeout(() => this._loadSummary(), 2000);
            }}
            onCancel={() => {
              this.setState({ showGodUserSettings: false });
            }}
            onFakeEventBucket={this._fakeEventBucket}
          />
        ) : null}
        <div style={{ display: "flex", fontSize: "12pt" }}>
          <div
            style={{
              width: "30%",
              minWidth: 200,
              maxWidth: 300,
              marginRight: 30,
              paddingTop: 10
            }}
          >
            <h2 style={{ marginBottom: 20 }}>Eventive Billing Hub</h2>
            <p>
              Welcome to the Eventive Billing Hub! You can use this page to view
              past and upcoming invoices.
              {UserStore.isGod() && summary && summary.meta ? (
                <div style={{ marginTop: 15, fontSize: "0.75rem" }}>
                  <Label>Eventive God Quick Controls</Label>
                  <div>
                    <LinkLike
                      onTouchTap={() =>
                        this.setState({ showGodUserSettings: true })
                      }
                    >
                      Edit Organization
                    </LinkLike>{" "}
                    &middot; <code>{user.id}</code>
                  </div>
                  <div>
                    <a
                      href={`https://dashboard.stripe.com/customers/${summary.meta.stripe_customer_id}`}
                      target="_blank"
                    >
                      View Stripe Customer
                    </a>{" "}
                    &middot; <code>{summary.meta.stripe_customer_id}</code>
                  </div>
                  <div>
                    <a
                      href={`https://dashboard.stripe.com/connect/accounts/${summary.meta.stripe_account_id}`}
                      target="_blank"
                    >
                      View Stripe Account
                    </a>{" "}
                    &middot;{" "}
                    {!summary.meta.stripe_account_id ? (
                      <InlineNoticeLabel
                        style={{
                          marginLeft: 2,
                          fontWeight: "bold",
                          backgroundColor: "orange"
                        }}
                        label="No account connected"
                      />
                    ) : summary.meta.is_platform_managed ? (
                      <InlineNoticeLabel
                        style={{
                          marginLeft: 2,
                          fontWeight: "bold",
                          backgroundColor: "green"
                        }}
                        label="Platform managed"
                      />
                    ) : (
                      <code>{summary.meta.stripe_account_id}</code>
                    )}
                  </div>
                </div>
              ) : null}
            </p>
            {summary && user ? (
              <div>
                <div>
                  <Label>Organization</Label>
                  <div>{user.organization}</div>
                  <div>
                    <code
                      style={{
                        fontWeight: "bold",
                        backgroundColor: "rgba(0,0,0,0.1)",
                        padding: "2px 4px",
                        fontSize: 12
                      }}
                    >
                      {UserStore.getUserId()}
                    </code>
                  </div>
                </div>
                <div style={{ marginTop: 20 }}>
                  <Label>Account primary currency</Label>
                  <div>{user.currency.toUpperCase()}</div>
                </div>
                <div style={{ marginTop: 20 }}>
                  <Label>Eventive Virtual subscription</Label>
                  {this.state.isUpdating ? (
                    <div>Loading...</div>
                  ) : user.virtual_festival_plan === "distributor" &&
                    user.virtual_festival_plan_status === "active" ? (
                    <div>
                      <div>
                        <strong>
                          <span
                            style={{
                              backgroundColor: "#4CAF50",
                              padding: "2px 4px",
                              color: "white"
                            }}
                          >
                            Your Distributor plan is active.
                          </span>{" "}
                          Each new film ingestion is subject to a US$100 fee.
                        </strong>
                      </div>
                      <div
                        style={{
                          fontSize: 12,
                          lineHeight: 1.2,
                          marginTop: 5,
                          color: "rgba(0,0,0,0.7)"
                        }}
                      >
                        If you wish to upgrade your plan for unlimited use of
                        the platform, please{" "}
                        <LinkLike
                          onTouchTap={() =>
                            Intercom(
                              "showNewMessage",
                              `I would like to speak with the sales team about upgrading my Eventive Distributor account. `
                            )
                          }
                        >
                          contact us
                        </LinkLike>
                        .
                      </div>
                    </div>
                  ) : user.virtual_festival_plan === "single_film" &&
                    user.virtual_festival_plan_status === "active" ? (
                    <div>
                      <div>
                        <strong>
                          <span
                            style={{
                              backgroundColor: "#4CAF50",
                              padding: "2px 4px",
                              color: "white"
                            }}
                          >
                            Your Single Film plan is active.
                          </span>{" "}
                          Each film is subject to a US$299 fee.
                        </strong>
                      </div>
                      <div
                        style={{
                          fontSize: 12,
                          lineHeight: 1.2,
                          marginTop: 5,
                          color: "rgba(0,0,0,0.7)"
                        }}
                      >
                        If you wish to upgrade your plan for unlimited use of
                        the platform, please{" "}
                        <LinkLike
                          onTouchTap={() =>
                            Intercom(
                              "showNewMessage",
                              `I would like to speak with the sales team about upgrading my Eventive Single Film account to a full activation! `
                            )
                          }
                        >
                          contact us
                        </LinkLike>
                        .
                      </div>
                      {/* <div style={{ fontSize: 12, marginTop: 5 }}>
                        <LinkLike>
                          Download 10/3/2020 receipt for US$299.00
                        </LinkLike>
                      </div> */}
                    </div>
                  ) : summary.subscription_data &&
                    summary.subscription_data.outstanding_invoice ? (
                    <div>
                      <strong>
                        <span
                          style={{
                            backgroundColor: "orange",
                            padding: "2px 4px",
                            color: "white"
                          }}
                        >
                          It's time to renew!
                        </span>{" "}
                        Your US
                        {formatPrice(
                          summary.subscription_data.outstanding_invoice
                            .amount_due,
                          { currency: "usd", locale: "en-US" }
                        )}{" "}
                        invoice is pending &ndash;{" "}
                        {moment(
                          summary.subscription_data.outstanding_invoice.date_due
                        ) > Date.now()
                          ? `${moment(
                              summary.subscription_data.outstanding_invoice
                                .date_due
                            )
                              .startOf("day")
                              .from(moment().startOf("day"), true)} left to pay`
                          : `payment is now past due`}
                        .
                      </strong>
                      <div style={{ marginTop: 10, marginBottom: 5 }}>
                        <RaisedButton
                          primary
                          label="Renew Eventive Virtual"
                          onClick={() => {
                            window.open(
                              summary.subscription_data.outstanding_invoice
                                .link,
                              "_blank"
                            );
                          }}
                        />
                      </div>
                      <div style={{ fontSize: 12, marginTop: 0 }}>
                        <LinkLike
                          onTouchTap={() => {
                            if (
                              window.confirm(
                                `Are you sure? Your access to Eventive Virtual will be terminated immediately.`
                              )
                            ) {
                              this.updateSubscription({
                                cancel: true
                              });
                            }
                          }}
                        >
                          Cancel subscription immediately
                        </LinkLike>
                      </div>
                    </div>
                  ) : summary.subscription_data &&
                    !summary.subscription_data.should_autorenew ? (
                    <div>
                      <div>
                        <strong>
                          <span
                            style={{
                              backgroundColor: "#4CAF50",
                              padding: "2px 4px",
                              color: "white"
                            }}
                          >
                            Your subscription is active!
                          </span>{" "}
                          Your plan will expire{" "}
                          {moment(
                            summary.subscription_data.current_period_end
                          ).fromNow()}
                          .
                        </strong>
                      </div>
                      <div
                        style={{
                          fontSize: 12,
                          lineHeight: 1.2,
                          marginTop: 5,
                          color: "rgba(0,0,0,0.7)"
                        }}
                      >
                        On{" "}
                        {moment(
                          summary.subscription_data.current_period_end
                        ).format("LL")}
                        , your subscription will be immediately canceled and all
                        virtual screenings will be disabled. Enable renewal
                        below to avoid this.
                      </div>
                      {latestInvoiceBlurb}
                      <div style={{ fontSize: 12, marginTop: 0 }}>
                        <LinkLike
                          onTouchTap={() =>
                            this.updateSubscription({
                              should_autorenew: true
                            })
                          }
                        >
                          Enable subscription renewal
                        </LinkLike>
                      </div>
                    </div>
                  ) : summary.subscription_data &&
                    summary.subscription_data.should_autorenew ? (
                    <div>
                      <div>
                        <strong>
                          <span
                            style={{
                              backgroundColor: "#4CAF50",
                              padding: "2px 4px",
                              color: "white"
                            }}
                          >
                            Your subscription is active!
                          </span>{" "}
                          Your plan will renew{" "}
                          {moment(
                            summary.subscription_data.current_period_end
                          ).fromNow()}
                          .
                        </strong>
                      </div>
                      <div
                        style={{
                          fontSize: 12,
                          lineHeight: 1.2,
                          marginTop: 5,
                          color: "rgba(0,0,0,0.7)"
                        }}
                      >
                        You will receive a renewal invoice on{" "}
                        {moment(
                          summary.subscription_data.current_period_end
                        ).format("LL")}
                        . If not paid within 30 days of receipt, your
                        subscription will be canceled.
                      </div>
                      {latestInvoiceBlurb}
                      <div style={{ fontSize: 12, marginTop: 0 }}>
                        <LinkLike
                          onTouchTap={() => {
                            if (
                              summary.subscription_data.subscription_schedule_id
                            ) {
                              Intercom(
                                "showNewMessage",
                                `I would like to cancel my scheduled subscription: `
                              );
                            } else {
                              this.updateSubscription({
                                should_autorenew: false
                              });
                            }
                          }}
                        >
                          Cancel subscription (disable renewal)
                        </LinkLike>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <strong>No current Eventive Virtual subscription.</strong>{" "}
                      <a href="https://eventive.org/virtual" target="_blank">
                        Learn more.
                      </a>
                      <div style={{ marginTop: 10, marginBottom: 5 }}>
                        <RaisedButton
                          primary
                          label="Activate Eventive Virtual"
                          onClick={() => this.activateVirtual()}
                        />
                      </div>
                    </div>
                  )}
                </div>
                {!this.state.isUpdating ? (
                  <div style={{ fontSize: 12, marginTop: 0 }}>
                    <LinkLike
                      onTouchTap={() => {
                        this.manageVirtual();
                      }}
                    >
                      View past receipts
                    </LinkLike>
                  </div>
                ) : null}
                {summary.ingest_credit_remaining ? (
                  <div style={{ marginTop: 20 }}>
                    <Label>Remaining Eventive Virtual ingestion credit</Label>
                    <div>
                      US
                      {formatPrice(summary.ingest_credit_remaining, {
                        currency: "usd",
                        locale: "en-US"
                      })}
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        lineHeight: 1.2,
                        marginTop: 5,
                        color: "rgba(0,0,0,0.7)"
                      }}
                    >
                      The above credit will be automatically applied to your
                      incurred content ingestion fees.
                    </div>
                  </div>
                ) : null}
                {/* <div style={{ marginTop: 20 }}>
                  <Label>Eventive Ticketing pricing plan</Label>
                  <div>Standard</div>
                </div> */}
                {/* <div style={{ marginTop: 20 }}>
                  <Label>Billing contact</Label>
                  <Creatable
                    multi={true}
                    value={this.state.billingEmails}
                    label="Billing invoice recipients"
                    placeholder="Add a billing contact..."
                    options={this.state.billingEmails.map(s => ({
                      label: s,
                      value: s
                    }))}
                    promptTextCreator={s => `Add new billing email "${s}"`}
                    onChange={selected =>
                      this.setState({
                        billingEmails: selected.map(x => x.value)
                      })
                    }
                  />
                </div> */}
              </div>
            ) : (
              <div style={{ marginTop: 60, textAlign: "center" }}>
                <p>Loading summary...</p>
                <WorkingIndicator centered />
              </div>
            )}
          </div>
          <div style={{ flex: 1 }}>
            <h3 style={{ display: "flex", alignItems: "center" }}>
              <span style={{ whiteSpace: "nowrap" }}>
                Your Eventive invoice for
              </span>
              <div
                style={{
                  display: "inline-block",
                  width: 250,
                  marginLeft: 10
                }}
              >
                <Select
                  clearable={false}
                  value={this.state.selectedMonth}
                  options={months}
                  onChange={e =>
                    this.setState({ selectedMonth: e.value }, () => {
                      this.context.router.replace(`/billing?month=${e.value}`);
                      this._loadInvoicePreview();
                    })
                  }
                />
              </div>
            </h3>
            {!invoicePreview ? (
              <div style={{ marginTop: 60, textAlign: "center" }}>
                <p>Generating invoice...</p>
                <WorkingIndicator centered />
              </div>
            ) : (
              <div>
                {invoicePreview && invoicePreview.finalized_at ? (
                  <p>
                    Thank you for partnering with Eventive! This is your
                    Eventive invoice for {invoiceLabel}. If you have any
                    questions, please{" "}
                    <LinkLike
                      onTouchTap={() =>
                        Intercom(
                          "showNewMessage",
                          `I have a question about my ${invoiceLabel} invoice: `
                        )
                      }
                    >
                      send us a message
                    </LinkLike>
                    .
                  </p>
                ) : (
                  <p>
                    This a <strong>PREVIEW ONLY</strong> of your upcoming
                    Eventive invoice for{" "}
                    {moment(this.state.selectedMonth, "MM-YYYY").format(
                      "MMMM YYYY"
                    )}
                    . Your final invoice may change,{" "}
                    <strong>PERHAPS SIGNIFICANTLY</strong>, due to a number of
                    possible factors, including (but not limited to) rebate of
                    never-watched deferred unlock fees, free ingest credits
                    included in your plan, premium services purchased that are
                    not listed here, etc.
                  </p>
                )}
                <div
                  style={{
                    display: "inline-block",
                    borderRadius: 5,
                    padding: 15,
                    border: "1px dashed #ccc",
                    marginBottom: 10
                  }}
                >
                  Status:{" "}
                  {invoicePreview.invoice_status === "paid" ? (
                    <strong>
                      Payment received &ndash; thank you!
                      {invoicePreview.invoice_pdf_url ? (
                        <span>
                          {" "}
                          &middot;{" "}
                          <a
                            href={invoicePreview.invoice_pdf_url}
                            target="_blank"
                          >
                            Download invoice PDF
                          </a>
                        </span>
                      ) : null}
                    </strong>
                  ) : invoicePreview.invoice_status === "uncollectible" ||
                    invoicePreview.invoice_status === "void" ? (
                    <strong>
                      Invoice marked {invoicePreview.invoice_status}
                    </strong>
                  ) : invoicePreview.finalized_at ? (
                    <strong>
                      Finalized{" "}
                      {moment(invoicePreview.finalized_at).format("lll")}
                    </strong>
                  ) : (
                    <strong>Not yet finalized</strong>
                  )}
                  {invoicePreview.finalized_at &&
                  (invoicePreview.invoice_status === "draft" ||
                    invoicePreview.invoice_status === "open" ||
                    invoicePreview.invoice_status === "not_yet_sent") ? (
                    invoicePreview.invoice_payment_url ? (
                      <div>
                        <div>Payment terms: Due in 30 days</div>
                        <div style={{ marginTop: 10 }}>
                          <RaisedButton
                            primary
                            label="Pay this invoice..."
                            onClick={() => {
                              window.location.href =
                                invoicePreview.invoice_payment_url;
                            }}
                          />
                        </div>
                        {invoicePreview.invoice_pdf_url ? (
                          <div style={{ marginTop: 10 }}>
                            <a
                              href={invoicePreview.invoice_pdf_url}
                              target="_blank"
                            >
                              Download invoice PDF
                            </a>
                          </div>
                        ) : null}
                      </div>
                    ) : (
                      <div>
                        Payment link and invoice PDF will be available shortly.
                      </div>
                    )
                  ) : null}
                  {isBillingGod ? (
                    <div style={{ marginTop: 10 }}>
                      <RaisedButton
                        primary={!invoicePreview.finalized_at}
                        disabled={
                          this.state.isFinalizing ||
                          invoicePreview.invoice_payment_url
                        }
                        label={
                          invoicePreview.invoice_payment_url
                            ? "Invoice has been sent"
                            : invoicePreview.finalized_at
                            ? "Re-finalize and overwrite in Stripe"
                            : "Finalize and push to Stripe"
                        }
                        onClick={() => {
                          if (
                            invoicePreview.finalized_at &&
                            !window.confirm(
                              "This will overwrite the invoice in Stripe. Are you sure?"
                            )
                          ) {
                            return;
                          }
                          this.finalize();
                        }}
                      />
                      {invoicePreview.stripe_invoice_id ? (
                        <RaisedButton
                          style={{ marginLeft: 10 }}
                          label={
                            invoicePreview.invoice_payment_url
                              ? "View in Stripe"
                              : "Send in Stripe"
                          }
                          onClick={() => {
                            window.open(
                              `https://dashboard.stripe.com/invoices/${invoicePreview.stripe_invoice_id}/edit`,
                              "_blank"
                            );
                          }}
                        />
                      ) : null}
                    </div>
                  ) : null}
                </div>
                <div style={{ marginBottom: 20 }}>
                  {this.state.loading && (
                    <div
                      style={{
                        position: "fixed",
                        textAlign: "center",
                        top: 64,
                        left: 256,
                        right: 0,
                        padding: 20,
                        backgroundColor: colors.green700,
                        color: "#fff",
                        zIndex: 999
                      }}
                    >
                      Loading updated invoice...
                    </div>
                  )}
                  <div>
                    <Label>Invoice display mode</Label>
                    <RadioButtonGroup
                      valueSelected={this.state.invoiceDisplayMode}
                      onChange={(e, v) =>
                        this.setState({ invoiceDisplayMode: v })
                      }
                    >
                      <RadioButton
                        style={{ display: "inline-block", width: 130 }}
                        value={"summary"}
                        label={"Summary"}
                      />
                      <RadioButton
                        style={{ display: "inline-block", width: 130 }}
                        value={"itemized"}
                        label={"Itemized"}
                      />
                    </RadioButtonGroup>
                  </div>
                </div>
                {Object.keys(lineItems).length === 0 ? (
                  <div>
                    <div
                      style={{
                        border: "1px solid #ccc",
                        borderRadius: "1em",
                        padding: 20,
                        fontWeight: "bold",
                        textAlign: "center"
                      }}
                    >
                      Your invoice for {invoiceLabel} is currently estimated to
                      be $0.00.
                    </div>
                    {isBillingGod && invoiceDisplayMode === "itemized" ? (
                      <div style={{ fontSize: 13 }}>
                        <AddLineItem
                          currency="usd"
                          onSubmit={({ description, amount }) => {
                            this.performAdjustment({
                              action: "create",
                              currency: "usd",
                              amount: amount,
                              description: description,
                              date: invoiceRawMonth._end
                            });
                          }}
                        />
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <div>
                    {Object.keys(lineItems).map(currency => {
                      return (
                        <div key={currency} style={{ marginTop: "1em" }}>
                          {Object.keys(lineItems).length > 1 ? (
                            <h4>
                              {currency.toUpperCase()}-denominated invoice
                              preview
                            </h4>
                          ) : null}
                          <div style={{ fontSize: 13 }}>
                            <GenericAnalyticsTable
                              csvLabel="eventive-invoice"
                              csvLabelDetail={this.state.selectedMonth}
                              noSort
                              downloadSubtle
                              downloadContainerStyle={{
                                marginTop: "-2.5em",
                                marginLeft: "1.5em"
                              }}
                              data={lineItems[currency]
                                .concat([
                                  {
                                    type: "total",
                                    currency,
                                    amount: lineItems[currency].reduce(
                                      (t, i) => t + i.amount,
                                      0
                                    )
                                  }
                                ])
                                .map(line_item => {
                                  return {
                                    category: {
                                      value:
                                        line_item.type === "total"
                                          ? "Total"
                                          : line_item.category,
                                      label: (
                                        <span style={{ whiteSpace: "nowrap" }}>
                                          {line_item.category}
                                        </span>
                                      )
                                    },
                                    amount: {
                                      value: line_item.amount / 100,
                                      label: (
                                        <div
                                          style={
                                            line_item.type === "total"
                                              ? {
                                                  fontWeight: "bold",
                                                  fontSize: "1.25em",
                                                  paddingTop: 5,
                                                  paddingBottom: 5
                                                }
                                              : {}
                                          }
                                        >
                                          {formatPrice(line_item.amount, {
                                            currency: line_item.currency,
                                            accounting: true
                                          })}
                                        </div>
                                      )
                                    },
                                    description: {
                                      value:
                                        line_item.type === "total"
                                          ? ""
                                          : `${line_item.description}${
                                              line_item.subtitle
                                                ? ` (${line_item.subtitle})`
                                                : ""
                                            }`,
                                      label: (
                                        <div style={{ lineHeight: 1.2 }}>
                                          <div>
                                            {Array.isArray(
                                              line_item.description
                                            )
                                              ? line_item.description.join(", ")
                                              : line_item.description}
                                          </div>
                                          {isBillingGod &&
                                          line_item.editable_id ? (
                                            <div
                                              style={{
                                                fontSize: "0.8em",
                                                marginTop: 1
                                              }}
                                            >
                                              <LinkLike
                                                onTouchTap={() =>
                                                  this.setState({
                                                    isEditingLineItem: {
                                                      [line_item.currency]:
                                                        line_item
                                                    }
                                                  })
                                                }
                                              >
                                                Edit line item
                                              </LinkLike>{" "}
                                              &middot;{" "}
                                              <LinkLike
                                                onTouchTap={() => {
                                                  if (
                                                    window.confirm(
                                                      "Are you sure you want to delete this line item?"
                                                    )
                                                  ) {
                                                    this.performAdjustment({
                                                      action: "delete",
                                                      target:
                                                        line_item.editable_id
                                                    });
                                                  }
                                                }}
                                              >
                                                Delete line item
                                              </LinkLike>
                                            </div>
                                          ) : line_item.subtitle ||
                                            line_item.related_link ||
                                            line_item.count ||
                                            (isBillingGod &&
                                              line_item.amount > 0 &&
                                              line_item.type !== "total") ? (
                                            <div
                                              style={{
                                                fontSize: "0.8em",
                                                marginTop: 1
                                              }}
                                            >
                                              {line_item.count ? (
                                                `${line_item.count} items included`
                                              ) : (
                                                <span>
                                                  {isBillingGod &&
                                                  !line_item.subtitle &&
                                                  !line_item.related_link
                                                    ? "Custom"
                                                    : null}
                                                  {line_item.subtitle}{" "}
                                                  {line_item.related_link ? (
                                                    <span>
                                                      {" "}
                                                      &middot;{" "}
                                                      <Link
                                                        to={
                                                          line_item.related_link
                                                        }
                                                        target="_blank"
                                                      >
                                                        View
                                                      </Link>
                                                    </span>
                                                  ) : null}
                                                  {isBillingGod &&
                                                  line_item.amount > 0 ? (
                                                    <span>
                                                      {" "}
                                                      &middot;{" "}
                                                      {this.state
                                                        .isAttemptingComp ===
                                                      line_item ? (
                                                        <span>
                                                          Comp{" "}
                                                          {formatPrice(
                                                            line_item.amount,
                                                            {
                                                              currency:
                                                                line_item.currency
                                                            }
                                                          )}
                                                          ?{" "}
                                                          <LinkLike
                                                            onTouchTap={() =>
                                                              this.performAdjustment(
                                                                {
                                                                  action:
                                                                    "create",
                                                                  currency:
                                                                    line_item.currency,
                                                                  amount:
                                                                    -line_item.amount,
                                                                  description: `Comp of ${line_item.description}`,
                                                                  date: invoiceRawMonth._end,
                                                                  line_item:
                                                                    line_item
                                                                }
                                                              )
                                                            }
                                                            style={{
                                                              fontWeight: "bold"
                                                            }}
                                                          >
                                                            Confirm
                                                          </LinkLike>{" "}
                                                          or{" "}
                                                          <LinkLike
                                                            onTouchTap={() =>
                                                              this.setState({
                                                                isAttemptingComp:
                                                                  null
                                                              })
                                                            }
                                                          >
                                                            Nope
                                                          </LinkLike>
                                                        </span>
                                                      ) : (
                                                        <LinkLike
                                                          onTouchTap={() =>
                                                            this.setState({
                                                              isAttemptingComp:
                                                                line_item
                                                            })
                                                          }
                                                        >
                                                          QuickComp
                                                        </LinkLike>
                                                      )}
                                                    </span>
                                                  ) : null}
                                                </span>
                                              )}
                                            </div>
                                          ) : null}
                                        </div>
                                      )
                                    }
                                  };
                                })}
                              columns={[
                                { label: "Category", value: "category" },
                                {
                                  label: "Description",
                                  value: "description"
                                },
                                { label: "Amount", value: "amount" }
                              ]}
                            />
                            {isBillingGod &&
                            invoiceDisplayMode === "itemized" ? (
                              <div>
                                <AddLineItem
                                  currency={currency}
                                  initialItem={
                                    (this.state.isEditingLineItem &&
                                      this.state.isEditingLineItem[currency]) ||
                                    undefined
                                  }
                                  onSubmit={({ description, amount }) => {
                                    let editingItem =
                                      this.state.isEditingLineItem &&
                                      this.state.isEditingLineItem[currency];

                                    if (editingItem) {
                                      this.performAdjustment({
                                        action: "edit",
                                        target: editingItem.editable_id,
                                        amount: amount,
                                        description: description
                                      });
                                    } else {
                                      this.performAdjustment({
                                        action: "create",
                                        currency: currency,
                                        amount: amount,
                                        description: description,
                                        date: invoiceRawMonth._end
                                      });
                                    }
                                  }}
                                />
                              </div>
                            ) : null}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
}

export default connectToStores(Billing);



// WEBPACK FOOTER //
// ./src/app/components/billing.jsx